<template>
  <DModal v-if="activeAbsenceReason" :show-close="false" :isBgOverlay="true" @close="close">
    <div slot="insertComponent" class="text-dark">
      <div class="d-modal__header">
        <h2><span v-if="isActiveAbsenceReasonIsReadyToDelete">{{
            $t('vue2.subs_ongoing_absence.delete_absence')
          }}</span><span
          v-else>{{ $t('vue2.subs_ongoing_absence.change_start_and_end_date') }}</span></h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="close"><span
          class="icon-cross2"></span></div>
      </div>
      <div class="d-modal__body d-modal__body--edit-absence" :class="{'d-modal__body--edit-absence--with-opus': isShowSalaryInfoBox}">
        <div class="d-modal__body--edit-absence__header">
          <img :src="showUser.default_thumb_url" alt=""/>
          <div class="absent-user__name">
            <h3>{{ showUser.name }}</h3>
            <div class="reason">
              <p>{{ getReasonTitle(activeAbsenceReason) }}</p>
              <p>
                <span v-if="isOngoing && isAbsenceOngoing" class="badge badge--orange badge--small">{{
                    $t('vue2.subs_ongoing_absence.ongoing_absence')
                  }}</span>
                <span v-if="!isOngoing && getIsAbsenceMultipleDays" class="badge badge--small"> {{
                    $t('vue2.substitution_user.multiple_days')
                  }}</span>
              </p>
            </div>
            <p>
              <span v-if="getSerialListItem">
              {{ formatDate(getSerialListItem.from_date) }}
              - {{ formatDate(getSerialListItem.to_date) }}, {{
                  getSerialListItem.from_time | showTime
                }} - {{ getSerialListItem.to_time | showTime }}
            </span>
              <span v-else>
              {{ activeAbsenceReason.start | showDate }} -
              <span>{{ activeAbsenceReason.end | showTime }}</span>
            </span>
            </p>
          </div>
        </div>
        <div class="d-modal__body--edit-absence__flex">
          <div class="d-modal__body--edit-absence__left">
            <div class="d-modal__body--edit-absence__body">
              <div class="d-modal__page-1" v-if="!isActiveAbsenceReasonIsReadyToDelete">
                <div class="do-warning marg-1-bottom" v-if="hasSentSdFravaer">
                  Fraværet er allerede sendt til SD. Ændrer du datoerne, sendes en rettelse ved næste
                  automatiske indberetning. Andre ændringer, fx af årsagen, rettes ikke automatisk og
                  skal ændres direkte i SD.<template v-if="hasDraftSdFravaer && !isReasonChangedAwayFromSd">
                  Usendte indberetninger i udbakken opdateres med dine ændringer.</template>
                </div>
                <div class="do-warning marg-1-bottom" v-if="isReasonChangedAwayFromSd">
                  Den valgte årsag har ingen SD-kode, men fraværet har usendte SD-indberetninger i
                  udbakken. De opdateres ikke ved årsagsskift og vil stadig blive sendt med den
                  oprindelige årsag. Slet dem i udbakken under Løn og Fravær &rarr;
                  Fraværsindberetninger, hvis de ikke skal sendes.
                </div>
                <div class="d-modal__body--edit-absence__body__input-group" v-if="reason">
                  <label class="d-label marg-1-bottom">{{ $t('vue2.substitution.reason') }}</label>
                  <div class="absence-reason">
                    <div>
                      <DSelect :items="subsAbsenceReasonsSorted"
                               :multiple="false"
                               :items-selected="reason"
                               @update="setReason"
                               :placeholder="$t('vue2.substitution.choose_absence_reason')">
                        <template #item-badge="{ item }" v-if="isAbsenceIntegrationActive">
                          <span class="do-opus-badge" v-if="item.kmd_absence_code_id">OPUS</span>
                          <span class="do-sd-badge" v-if="item.sd_absence_code_id">SD</span>
                        </template>
                        <div slot="noMatch">
                          <h4>
                            {{ $t('vue2.dselect.no_matching_results') }}
                          </h4>
                        </div>
                      </DSelect>
                    </div>
                  </div>
                  <span class="error-message" v-if="showErrors">{{ $t('vue2.substitution.reason_must_be_filled') }}</span>
                </div>
                <div class="d-modal__body--edit-absence__body__input-group">
                  <label class="d-label marg-1-bottom">{{ $t('vue2.subs_ongoing_absence.date_label') }}</label>
                  <div class="flex">
                    <DatepickerRange
                      :allowSameDate="true"
                      :startdate="originalStart"
                      :enddate="originalEnd"
                      @startchanged="setStartDate"
                      @endchanged="setEndDate">
                    </DatepickerRange>
                    <div class="absence-no-end-date"
                         :class="[{'absence-no-end-date--active': isAbsenceOngoing}, {'absence-no-end-date--disabled': !isOngoingAbsenceAllowed}]">
                      <div class="absence-no-end-date--not-allowed" v-if="!isOngoingAbsenceAllowed"></div>
                      <div class="absence-no-end-date__inner" @click.stop.prevent="isAbsenceOngoing = !isAbsenceOngoing">
                        <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                        <label>{{ $t('vue2.subs_ongoing_absence.ongoing_absence') }}</label>
                      </div>
                      <DQuestionMark>
                        <p slot="answer">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                      </DQuestionMark>
                    </div>
                  </div>
                </div>
                <div class="d-modal__body--edit-absence__body__input-group">
                  <label class="d-label marg-1-bottom">{{ $t('vue2.substitution.time') }}</label>
                  <TimeChanger
                    :showIcon="true"
                    :start="startTime"
                    :end="endTime"
                    @updateStart="setStartTime"
                    @updateEnd="setEndTime">
                  </TimeChanger>
                </div>
              </div>
              <p v-if="deleteAbsenceFollowingDates.length > 0">
                <label class="d-label">{{ $t('vue2.subs_ongoing_absence.absence_will_be_deleted') }}</label>
                <span
                  v-for="(date, idx) in deleteAbsenceFollowingDates"><span
                  v-if="idx > 0">, </span> {{ formatDate(date) }}</span></p>
              <div class="do-warning marg-1-bottom" v-if="isActiveAbsenceReasonIsReadyToDelete && hasSdFravaer">
                <template v-if="hasSentSdFravaer">
                  Fraværet er allerede sendt til SD. Sendte indberetninger trækkes ikke tilbage, når
                  fraværet slettes, og skal korrigeres manuelt i SD.
                </template>
                <template v-if="hasDraftSdFravaer">
                  {{ hasSentSdFravaer ? 'Usendte indberetninger' : 'Fraværets usendte indberetninger' }}
                  i udbakken slettes sammen med fraværet.
                </template>
              </div>
              <div class="d-modal__page-2" v-if="originalDatesNotInNewRange.length > 0">
                <SubsDeleteAbsence
                  :calendarId="this.activeAbsenceReason.calendar_id"
                  :firstRange="originalDatesNotInNewRangeMinimum"
                  :lastRange="originalDatesNotInNewRangeMaximum"
                  :dates="originalDatesNotInNewRange"
                  :startTime="startTime"
                  :endTime="endTime"
                  @lessonsWithSubs="setLessonsWithSubs"
                  @gotLessons="isLessonsLoaded = true"
                  @changed="setDeleteSubs">
                </SubsDeleteAbsence>
              </div>
            </div>
          </div>
          <!-- SD / Opus sidebar: hvad der indberettes til lønsystemet -->
          <div class="edit-absence__opus-sidebar" v-if="isShowSalaryInfoBox">
            <div class="edit-absence__opus-loading" v-if="!isLoadedSalarySystemInfo">
              <div class="do-spinner"></div>
            </div>
            <div class="edit-absence__opus-content" v-if="isLoadedSalarySystemInfo && isShowSdInfoBox">
              <div class="do-sd-badge do-opus-badge--large opus-info__header">SD</div>

              <div class="opus-info__field">
                <label class="opus-info__label">Ansættelse</label>
                <select
                  class="do-select"
                  v-model="selectedEmploymentId"
                  @change="onEmploymentChange($event.target.value)">
                  <option value="">— Vælg ansættelsesnummer —</option>
                  <option
                    v-for="employment in userEmployments"
                    :key="employment.id"
                    :value="employment.id">
                    {{ employment.title }} - {{ employment.employment_number || employment.id }}
                  </option>
                </select>
                <span class="error-message" v-if="!isEmploymentSelected && showErrors">
                  Ansættelsesnummer skal vælges
                </span>
              </div>

              <div class="opus-info__field">
                <label class="opus-info__label">Fraværsårsag</label>
                <div class="opus-info__reason-display">
                  <span class="opus-info__reason-code" v-if="getSDAbsence">
                    {{ getSDAbsence.loenart }}{{ getSDAbsence.title ? ' - ' + getSDAbsence.title : '' }}
                  </span>
                </div>
              </div>

              <div class="opus-info__field">
                <label class="opus-info__label">Dato</label>
                <div class="opus-info__date-text">
                  <span v-if="isAbsenceOngoing">{{ startDateFormatted }} - Åbenstående</span>
                  <span v-else>{{ dateRangeFormatted }} <span class="opus-info__date-days">({{ dateRangeDaysFormatted }})</span></span>
                </div>
                <div v-if="isOngoingAbsenceAllowed" class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <span>Indberet som åbenstående</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
              </div>

              <div class="opus-info__field">
                <label class="opus-info__label">Tid</label>
                <div class="opus-info__date-text" v-if="useTime">
                  {{ startTime.format('HH:mm') }} - {{ endTime.format('HH:mm') }} <span class="opus-info__date-days">({{ timeRangeHoursFormatted }})</span>
                </div>
                <div class="opus-info__date-text" v-else>
                  Hel dag
                </div>
                <div class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click="useTime = !useTime">
                    <app-toggle :prop-on="!useTime"></app-toggle>
                    <span>Indberet som hel dag</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">Toggle fra for at indberette timebaseret fravær, hvis fraværet kun gælder en del af dagen.</p>
                  </DQuestionMark>
                </div>
              </div>
            </div>
            <div class="edit-absence__opus-content" v-if="isLoadedSalarySystemInfo && isShowOpusInfoBox">
              <div class="do-opus-badge do-opus-badge--large opus-info__header">Opus</div>

              <!-- Ansættelse -->
              <div class="opus-info__field">
                <label class="opus-info__label">Ansættelse</label>
                <select
                  class="do-select"
                  v-model="selectedEmploymentId"
                  @change="onEmploymentChange($event.target.value)">
                  <option value="">— Vælg ansættelsesnummer —</option>
                  <option
                    v-for="employment in userEmployments"
                    :key="employment.id"
                    :value="employment.id">
                    {{ employment.title }} - {{ employment.employment_number || employment.id }}
                  </option>
                </select>
                <span class="error-message" v-if="!isEmploymentSelected && showErrors">
                  Ansættelsesnummer skal vælges
                </span>
              </div>

              <!-- Fraværsårsag -->
              <div class="opus-info__field">
                <label class="opus-info__label">Fraværsårsag</label>
                <div class="opus-info__reason-display">
                  <span class="opus-info__reason-code" v-if="getOPUSAbsence">
                    {{ getOPUSAbsence.absence_type }}{{ getOPUSAbsence.absence_type_text ? ' - ' + getOPUSAbsence.absence_type_text : '' }}
                  </span>
                </div>
                <div v-if="isShowExtraInformation" class="opus-info__extra-info">
                  <!-- Barsel -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Maternity'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til barselssager</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.MaternityCaseKey" :value="option.MaternityCaseKey">
                        {{ option.ExpectedDeliveryDate }} - {{ option.MaternityCaseKey }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen barselssager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- Tjenestefri -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Reason'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til tjenestefri</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.ReasonCode" :value="option.ReasonCode">
                        {{ option.ReasonCodeText }} - {{ option.ReasonCode }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen tjenestefri årsager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- Arbejdsskade -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'WorkInjury'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til arbejdsskader</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.AccidentNumber" :value="option.AccidentNumber">
                        {{ option.AccidentDate }} - {{ option.AccidentNumber }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen arbejdsskadesager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- Omsorgsdage -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Child'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til omsorgsdage</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.ChildNumber" :value="option.ChildNumber">
                        {{ option.FirstName }} {{ option.LastName }} - {{ option.ChildNumber }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen barn tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <span class="error-message" v-if="!isAdditionalDataSelected && showErrors">
                    {{ additionalDataValidationError }}
                  </span>
                </div>
              </div>

              <!-- Dato -->
              <div class="opus-info__field">
                <label class="opus-info__label">Dato</label>
                <div class="opus-info__date-text">
                  <span v-if="isAbsenceOngoing">{{ startDateFormatted }} →</span>
                  <span v-else>{{ dateRangeFormatted }} <span class="opus-info__date-days">({{ dateRangeDaysFormatted }})</span></span>
                </div>
                <div v-if="isOpusAllowOpen" class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <span>Indberet som åbenstående</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
                <p class="opus-info__note--muted" v-else-if="!isOpusAllowOpen">
                  Åbenstående fravær er ikke muligt for denne fraværsårsag
                </p>
              </div>

              <!-- Tid -->
              <div class="opus-info__field">
                <label class="opus-info__label">Tid</label>
                <div class="opus-info__date-text" v-if="useTime">
                  {{ startTime.format('HH:mm') }} - {{ endTime.format('HH:mm') }} <span class="opus-info__date-days">({{ timeRangeHoursFormatted }})</span>
                </div>
                <div class="opus-info__date-text" v-else>
                  Hel dag
                </div>
                <div class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click="useTime = !useTime">
                    <app-toggle :prop-on="!useTime"></app-toggle>
                    <span>Indberet som hel dag</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">Toggle fra for at indberette timebaseret fravær, hvis fraværet kun gælder en del af dagen.</p>
                  </DQuestionMark>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-modal__buttons d-modal__body--edit-absence__actions">
          <div>
            <button class="bttn bttn-danger marg-5-right" v-if="!isActiveAbsenceReasonIsReadyToDelete"
                    @click="$store.commit('setIsActiveAbsenceReasonIsReadyToDelete', true)"><span
              class="icon-bin"></span>
            </button>
          </div>
          <div class="d-modal__body--edit-absence__actions__right">
            <button class="marg-5-right" @click="close">{{ $t('vue2.common.cancel') }}</button>
            <!-- Fravær skal slettes -->
            <template v-if="isActiveAbsenceReasonIsReadyToDelete">
              <button @click="removeSubsAndDeleteAbsence" class="submit-button submit-button--danger">
                <!-- Fjern vikarer -->
                <span v-if="eventsToRemovedSubs.length > 0">{{
                    $t('vue2.subs_ongoing_absence.remove_subs_and_delete_absence')
                  }}</span>
                <!-- Der er vikarer men ikke fjern dem -->
                <span v-if="lessonsWithSubs.length > 0 && eventsToRemovedSubs.length < 1">{{
                    $t('vue2.subs_ongoing_absence.keep_subs_and_delete_absence')
                  }}</span>
                <!-- Ingen vikarer -->
                <span v-if="lessonsWithSubs.length < 1 && eventsToRemovedSubs.length < 1">{{
                    $t('vue2.subs_ongoing_absence.delete_absence')
                  }}</span>
              </button>
            </template>
            <!-- Fravær skal opdateres -->
            <template v-else>
              <!-- Måske skal der fjernes vikarer -->
              <button v-if="originalDatesNotInNewRange.length > 0"
                      class="submit-button"
                      @click="removeSubsAndUpdateAbsence">
                {{ $t('vue2.common.update') }}
              </button>
              <!-- Der skal ikke fjernes vikarer -->
              <button
                v-if="originalDatesNotInNewRange.length < 1 && this.eventsToRemovedSubs.length < 1"
                @click="updateDates"
                class="submit-button">
                {{ $t('vue2.common.update') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/da'
import {mapGetters} from 'vuex'
import moment from 'moment'
import SubsDeleteAbsence from './SubsDeleteAbsenceSubs'
import DatepickerRange from '../datepicker/DatePickerRange'
import SubsUserAbsenceReason from './SubsUserAbsenceReason'
import AppToggle from '../common/AppToggle'
import DQuestionMark from '../common/DQuestionMark'
import TimeChanger from '../TimeChanger'
import DSelect from '../DSelect/DSelect'
import docendo from '../../docendo'
import eventbus from '../../eventbus'

export default {
  components: {
    DModal,
    DatePicker,
    SubsDeleteAbsence,
    DatepickerRange,
    SubsUserAbsenceReason,
    AppToggle,
    DQuestionMark,
    TimeChanger,
    DSelect
  },
  data () {
    return {
      originalStartAndEnd: [],
      eventsToRemovedSubs: [],
      isLessonsLoaded: false,
      datePickerStart: null,
      datePickerEnd: null,
      startAndEnd: [],
      isAbsenceOngoing: false,
      lessonsWithSubs: [],
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      reason: [],
      showErrors: false,
      // Opus-relaterede felter
      selectedEmploymentId: '',
      opusAbsenceCodes: [],
      isLoadedSalarySystemInfo: false,
      useTime: false,
      useTimeBeforeTimeEdit: false,
      wholeDayToastTimer: null,
      additionalDataOptions: [],
      selectedAdditionalData: '',
      isLoadingAdditionalData: false,
      hasLoadedAdditionalData: false,
      additionalDataError: null,
      additionalDataValidationError: null,
      hasReasonBeenChanged: false,
      sdAbsenceCodes: [],
      // null = ukendt (ikke hentet endnu), ellers fraværets SD-indberetninger
      sdFravaerForSerial: null
    }
  },
  filters: {
    showDate (date) {
      return moment(date).format('DD. MMM, LT')
    },
    showTime (date) {
      return moment(date).format('LT')
    },
    date (date) {
      return moment(date).format('ll')
    }
  },
  computed: {
    ...mapGetters([
      'allUsersInCalIdHash',
      'longAbsentListInHash',
      'subsAbsenceReasonsSorted',
      'employmentsInUserIdHash'
    ]),
    activeAbsenceReason () {
      return this.$store.state.subs.activeAbsenceReason
    },
    isActiveAbsenceReasonIsReadyToDelete () {
      return this.$store.state.subs.isActiveAbsenceReasonIsReadyToDelete
    },
    showUser () {
      return this.allUsersInCalIdHash[this.activeAbsenceReason.calendar_id]
        ? this.allUsersInCalIdHash[this.activeAbsenceReason.calendar_id]
        : {}
    },
    absenceReasons () {
      return this.$store.state.subs.absenceReasons
    },
    schoolId () {
      return window.SCHOOL ? window.SCHOOL.id : null
    },
    selectedReason () {
      return this.reason && this.reason[0] ? this.reason[0] : null
    },
    getOPUSAbsence () {
      if (!this.selectedReason || !this.selectedReason.kmd_absence_code_id) {
        return null
      }
      const opusAbsenceId = this.selectedReason.kmd_absence_code_id
      const absenceCodes = this.opusAbsenceCodes || []
      if (!Array.isArray(absenceCodes) || absenceCodes.length < 1) {
        return null
      }
      return absenceCodes.find(code => String(code.id) === String(opusAbsenceId)) || null
    },
    getSDAbsence () {
      if (!this.selectedReason || !this.selectedReason.sd_absence_code_id) {
        return null
      }
      const sdAbsenceId = this.selectedReason.sd_absence_code_id
      const absenceCodes = this.sdAbsenceCodes || []
      if (!Array.isArray(absenceCodes) || absenceCodes.length < 1) {
        return null
      }
      return absenceCodes.find(code => String(code.id) === String(sdAbsenceId)) || null
    },
    isOpusAbsenceActivated () {
      return this.$store.getters.schoolFeatures && this.$store.getters.schoolFeatures.indexOf('kmd_absence') > -1
    },
    isSDIntegrationActive () {
      return this.$store.getters.schoolFeatures && this.$store.getters.schoolFeatures.indexOf('silkeborg_data_integration') > -1
    },
    isAbsenceIntegrationActive () {
      return this.isOpusAbsenceActivated || this.isSDIntegrationActive
    },
    isShowOpusInfoBox () {
      if (!this.activeAbsenceReason || !this.isOpusAbsenceActivated || !this.selectedReason || !this.selectedReason.kmd_absence_code_id) return false
      // Vis kun sidebar hvis brugeren manuelt har ændret årsagen til en med kmd_absence_code_id
      if (this.activeAbsenceReason.kmd_absence_code_id === null && !this.hasReasonBeenChanged) return false
      return this.selectedReason && this.selectedReason.kmd_absence_code_id != null
    },
    isShowSdInfoBox () {
      return !!(this.isSDIntegrationActive && this.selectedReason && this.selectedReason.sd_absence_code_id)
    },
    isShowSalaryInfoBox () {
      return this.isShowOpusInfoBox || this.isShowSdInfoBox
    },
    isShowExtraInformation () {
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence || !opusAbsence.additional_data) { // Når vi skal have årsager på Tjenestetid
        return false
      }
      return !!this.selectedEmploymentId
    },
    existingAdditionalData () {
      if (this.activeAbsenceReason && this.activeAbsenceReason.additional_data) {
        return this.activeAbsenceReason.additional_data
      }
      const serialItem = this.getSerialListItem
      return serialItem && serialItem.additional_data ? serialItem.additional_data : ''
    },
    shouldShowEmploymentSelection () {
      return this.isAbsenceIntegrationActive && this.selectedReason && ((this.selectedReason.kmd_absence_code_id && this.isOpusAbsenceActivated) || (this.selectedReason.sd_absence_code_id && this.isSDIntegrationActive))
    },
    isEmploymentSelected () {
      if (!this.shouldShowEmploymentSelection) {
        return true
      }
      return this.selectedEmploymentId && this.selectedEmploymentId !== ''
    },
    isAdditionalDataRequired () {
      return this.getOPUSAbsence && this.getOPUSAbsence.additional_data_required
    },
    isAdditionalDataSelected () {
      if (!this.isAdditionalDataRequired) {
        return true
      }
      return this.selectedAdditionalData !== ''
    },
    isOpusAllowOpen () {
      if (!this.isOpusAbsenceActivated || !this.selectedReason || !this.selectedReason.kmd_absence_code_id) {
        return true
      }
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence || typeof opusAbsence.allow_open === 'undefined') {
        return true
      }
      return opusAbsence.allow_open === true
    },
    startDateFormatted () {
      return this.datePickerStart ? moment(this.datePickerStart).format('DD-MM-YYYY') : ''
    },
    dateRangeFormatted () {
      if (!this.datePickerStart || !this.datePickerEnd) return ''
      return moment(this.datePickerStart).format('DD-MM-YYYY') + ' - ' + moment(this.datePickerEnd).format('DD-MM-YYYY')
    },
    dateRangeDaysFormatted () {
      if (!this.datePickerStart || !this.datePickerEnd) return ''
      const days = moment(this.datePickerEnd).diff(moment(this.datePickerStart), 'days') + 1
      return days + (days === 1 ? ' dag' : ' dage')
    },
    timeRangeHoursFormatted () {
      const diff = moment(this.endTime).diff(moment(this.startTime), 'minutes')
      const hours = (diff / 60).toFixed(1)
      return hours + ' timer'
    },
    userEmployments () {
      const user = this.showUser
      if (!user || !user.id) {
        return []
      }
      const employments = this.employmentsInUserIdHash[user.id] || []
      return employments.filter(emp => emp.employment_number && String(emp.employment_number).trim() !== '')
    },
    originalStart () {
      return moment(this.originalStartAndEnd[0]).format('YYYY-MM-DD')
    },
    originalEnd () {
      return moment(this.originalStartAndEnd[1]).format('YYYY-MM-DD')
    },
    originalDatesArr () {
      return this.enumerateDaysBetweenDates(this.originalStart, this.originalEnd)
    },
    datePickerDatesArr () {
      let start = moment(this.datePickerStart).format('YYYY-MM-DD')
      let end = moment(this.datePickerEnd).format('YYYY-MM-DD')
      return this.enumerateDaysBetweenDates(start, end)
    },
    originalDatesNotInNewRange () {
      return this.originalDatesArr.filter(e => this.datePickerDatesArr.indexOf(e) === -1)
    },
    originalDatesNotInNewRangeMinimum () {
      let moments = this.originalDatesNotInNewRange.map(d => moment(d))
      let date = moment.min(moments)
      let datepickerStart = moment(this.datePickerStart).format('YYYY-MM-DD')
      if (this.datePickerStart) {
        return moment(date).isBefore(this.datePickerStart) ? [date, moment(datepickerStart).subtract(1, 'day')] : false
      } else {
        return false
      }
    },
    originalDatesNotInNewRangeMaximum () {
      let moments = this.originalDatesNotInNewRange.map(d => moment(d))
      let date = moment.max(moments)
      let datepickerEnd = moment(this.datePickerEnd).format('YYYY-MM-DD')
      return moment(date).isAfter(this.datePickerEnd) ? [moment(datepickerEnd).add(1, 'day'), date] : false
    },
    deleteAbsenceFollowingDates () {
      return this.originalDatesNotInNewRange
    },
    getSerialListItem () {
      return this.activeAbsenceReason.event_serial_id ? this.longAbsentListInHash[this.activeAbsenceReason.event_serial_id] : false
    },
    // Fraværets SD-indberetninger ({id, state}): helst sd_fravaer-arrayet fra
    // backendens list-payload, ellers resultatet af readSdFravaerForSerial.
    sdFravaerRows () {
      const serialItem = this.getSerialListItem
      if (serialItem && Array.isArray(serialItem.sd_fravaer)) return serialItem.sd_fravaer
      return this.sdFravaerForSerial || []
    },
    hasSdFravaer () {
      return this.sdFravaerRows.length > 0
    },
    // Er (dele af) fraværet allerede sendt til SD?
    hasSentSdFravaer () {
      return this.sdFravaerRows.some(row => row.state !== 'draft')
    },
    // Ligger der usendte indberetninger for fraværet i udbakken?
    hasDraftSdFravaer () {
      return this.sdFravaerRows.some(row => row.state === 'draft')
    },
    // Årsagen er ændret til én uden SD-kode, mens der ligger kladder i udbakken.
    // Backenden synkroniserer kun kladder når årsagen HAR en SD-kode, så kladderne
    // beholder den gamle årsag og sendes alligevel - det skal brugeren vide.
    isReasonChangedAwayFromSd () {
      if (!this.hasReasonBeenChanged || !this.hasDraftSdFravaer) return false
      return !(this.selectedReason && this.selectedReason.sd_absence_code_id)
    },
    isOngoing () {
      return this.getSerialListItem && this.getSerialListItem.ongoing
    },
    isOngoingAbsenceAllowed () {
      let endDate = moment(this.startAndEnd[1]).format('YYYY-MM-DD')
      let now = moment().format('YYYY-MM-DD')
      return moment(endDate).isSameOrAfter(now)
    }
  },
  watch: {
    isActiveAbsenceReasonIsReadyToDelete () {
      if (this.isActiveAbsenceReasonIsReadyToDelete) {
        this.datePickerStart = null
        this.datePickerEnd = null
      }
    },
    employmentsInUserIdHash () {
      if (this.shouldShowEmploymentSelection) {
        this.$nextTick(() => {
          this.autoSelectSingleEmployment()
        })
      }
    },
    isShowSalaryInfoBox (newValue) {
      if (newValue) {
        this.isLoadedSalarySystemInfo = false
        setTimeout(() => {
          this.isLoadedSalarySystemInfo = true
        }, 350)
      } else {
        this.isLoadedSalarySystemInfo = false
      }
    },
    isShowExtraInformation (newValue) {
      if (newValue) {
        this.fetchAdditionalDataForAbsence()
      } else {
        this.additionalDataOptions = []
        this.selectedAdditionalData = ''
        this.hasLoadedAdditionalData = false
        this.additionalDataError = null
      }
    }
  },
  methods: {
    formatDate (date) {
      return moment(date).format('ll')
    },
    getIsAbsenceMultipleDays () {
      let serialItem = this.getSerialListItem
      return serialItem && moment(serialItem.from_date).format('YYYY-MM-DD') !== moment(serialItem.to_date).format('YYYY-MM-DD')
    },
    setDeleteSubs (val) {
      this.eventsToRemovedSubs = val
    },
    enumerateDaysBetweenDates (startDate, endDate) {
      let date = []
      while (moment(startDate) <= moment(endDate)) {
        date.push(startDate)
        startDate = moment(startDate).add(1, 'days').format('YYYY-MM-DD')
      }
      return date
    },
    getReasonTitle (reason) {
      let idx = this.absenceReasons.findIndex(absenceSubject => absenceSubject.id === reason.subject_id)
      return idx > -1 ? this.absenceReasons[idx].name : ''
    },
    removeSubsAndUpdateAbsence () {
      const vuexObj = {
        events: this.eventsToRemovedSubs,
        userId: this.showUser.id,
        user_calendar_id: this.activeAbsenceReason.calendar_id
      }
      this.$store.dispatch('removeSubsFromEvents', vuexObj)
      this.updateDates()
    },
    updateDates () {
      const selectedReason = this.reason && this.reason[0]
      // Valider kun hvis sidebar er synlig (OPUS eller SD)
      if (this.isShowSalaryInfoBox) {
        if (this.shouldShowEmploymentSelection && !this.isEmploymentSelected) {
          this.showErrors = true
          return
        }
        if (this.isShowOpusInfoBox && !this.isAdditionalDataSelected) {
          this.showErrors = true
          this.additionalDataValidationError = 'Du skal vælge ekstra information'
          return
        }
      }
      let obj = JSON.parse(JSON.stringify(this.activeAbsenceReason)) // clone obj without reference
      obj['from_date'] = this.datePickerStart
      obj['to_date'] = this.datePickerEnd
      obj['ongoing'] = this.isAbsenceOngoing
      obj['to_time'] = this.endTime
      obj['from_time'] = this.startTime
      obj['use_time'] = this.useTime
      obj['subject_id'] = selectedReason ? selectedReason.id : this.activeAbsenceReason.subject_id
      obj['internal_note'] = this.activeAbsenceReason.internal_note || ''
      // Brug valgt employment hvis sidebar er synlig, ellers behold eksisterende
      if (this.isShowSalaryInfoBox && this.selectedEmploymentId) {
        obj['employment_id'] = this.selectedEmploymentId
      } else {
        obj['employment_id'] = this.activeAbsenceReason.employment_id || null
      }
      obj['additional_data'] = this.isShowExtraInformation ? (this.selectedAdditionalData || null) : null
      console.log('updateEventsSerial', obj)
      this.$store.dispatch('updateEventsSerial', obj)
      this.close()
    },
    close () {
      this.$store.commit('setActiveAbsenceReason', null)
      this.$store.commit('setIsActiveAbsenceReasonIsReadyToDelete', false)
    },
    removeSubsAndDeleteAbsence () {
      let obj = {
        events: this.eventsToRemovedSubs,
        userId: this.showUser.id,
        user_calendar_id: this.activeAbsenceReason.calendar_id
      }
      this.$store.dispatch('removeSubsFromEvents', obj)
      let reasonObj = {
        event_serial_id: this.activeAbsenceReason.event_serial_id,
        user_calendar_id: this.activeAbsenceReason.calendar_id,
        id: this.activeAbsenceReason.id
      }
      this.$store.dispatch('deleteAbsence', reasonObj)
      this.closeSchedulerPopover()
      this.close()
    },
    setStartDate (payload) {
      this.datePickerStart = moment(payload).format('YYYY-MM-DD')
      this.setStartAndEnd()
    },
    setEndDate (payload) {
      const newEnd = moment(payload).format('YYYY-MM-DD')
      const previousEnd = this.datePickerEnd
      this.datePickerEnd = newEnd
      this.setStartAndEnd()
      // DatepickerRange emitter ved mount — slå kun løbende fra når brugeren
      // selv sætter slutdatoen til i dag eller tidligere (raskmelding).
      const now = moment().format('YYYY-MM-DD')
      if (previousEnd && newEnd !== previousEnd && !moment(newEnd).isAfter(now)) {
        this.isAbsenceOngoing = false
      }
    },
    setStartTime (payload) {
      this.startTime = payload
      this.syncWholeDayWithStandardTimes()
    },
    setEndTime (payload) {
      this.endTime = payload
      this.syncWholeDayWithStandardTimes()
    },
    syncWholeDayWithStandardTimes () {
      const userSettings = this.$store.state.userSettings
      const saved = userSettings && userSettings.subSettingsCreateTime
      const standardStart = saved ? moment(saved.start).format('HH:mm') : '08:00'
      const standardEnd = saved ? moment(saved.end).format('HH:mm') : '16:00'
      const isStandardStart = moment(this.startTime).format('HH:mm') === standardStart
      const isStandardEnd = moment(this.endTime).format('HH:mm') === standardEnd
      if (this.wholeDayToastTimer === null) {
        this.useTimeBeforeTimeEdit = this.useTime
      }
      this.useTime = !(isStandardStart && isStandardEnd)
      this.notifyWholeDayChange()
    },
    notifyWholeDayChange () {
      clearTimeout(this.wholeDayToastTimer)
      this.wholeDayToastTimer = setTimeout(() => {
        this.wholeDayToastTimer = null
        if (this.isShowSalaryInfoBox && this.useTime !== this.useTimeBeforeTimeEdit) {
          eventbus.toast = {text: this.useTime ? '"Indberet som hel dag" er slået fra' : '"Indberet som hel dag" er slået til', success: true}
        }
      }, 800)
    },
    setStartAndEnd () {
      this.startAndEnd = [moment(this.datePickerStart).format('YYYY-MM-DD'), moment(this.datePickerEnd).format('YYYY-MM-DD')]
    },
    setLessonsWithSubs (arr) {
      this.lessonsWithSubs = arr
    },
    setReason (reason) {
      this.reason = reason
      this.showErrors = false
      this.hasReasonBeenChanged = true
      this.selectedEmploymentId = ''
      this.resetOpusFields()
      const selected = reason && reason[0]
      const shouldBeOngoing = !!(selected && selected.default_ongoing && this.isOpusAllowOpen && this.isOngoingAbsenceAllowed)
      if (shouldBeOngoing !== this.isAbsenceOngoing) {
        this.setIsAbsenceOngoing(shouldBeOngoing)
      }
      if (this.shouldShowEmploymentSelection) {
        this.$nextTick(() => {
          this.autoSelectSingleEmployment()
        })
      }
    },
    // Opus methods
    onEmploymentChange (value) {
      this.selectedEmploymentId = value
    },
    autoSelectSingleEmployment () {
      if (!this.shouldShowEmploymentSelection) {
        return
      }
      if (this.selectedEmploymentId) {
        return
      }
      if (this.userEmployments.length === 1) {
        this.selectedEmploymentId = this.userEmployments[0].id
      }
    },
    listKmdOpusAbsenceCodes () {
      docendo.listKmdOpusAbsenceCodes(this.schoolId, success => {
        this.opusAbsenceCodes = Array.isArray(success) ? success : []
      }, failure => {
        this.opusAbsenceCodes = []
      })
    },
    listSdAbsenceCodes () {
      docendo.listSilkeborgDataAbsenceCodes(this.schoolId, success => {
        this.sdAbsenceCodes = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
      }, failure => {
        this.sdAbsenceCodes = []
      })
    },
    resetOpusFields () {
      this.useTime = false
      this.additionalDataOptions = []
      this.selectedAdditionalData = ''
      this.hasLoadedAdditionalData = false
      this.additionalDataError = null
      this.additionalDataValidationError = null
    },
    setIsAbsenceOngoing (bool) {
      if (!this.isOpusAllowOpen && bool) {
        this.isAbsenceOngoing = false
        return
      }
      this.isAbsenceOngoing = bool
    },
    fetchAdditionalDataForAbsence () {
      if (!this.isShowExtraInformation) {
        this.additionalDataOptions = []
        this.selectedAdditionalData = ''
        return
      }
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence) return

      const employmentId = this.selectedEmploymentId
      if (!employmentId) return

      this.isLoadingAdditionalData = true
      this.hasLoadedAdditionalData = false
      this.additionalDataOptions = []
      this.selectedAdditionalData = ''

      docendo.listAdditionalDataForAbsence(
        this.schoolId,
        employmentId,
        opusAbsence.id,
        moment(this.datePickerStart).format('YYYY-MM-DD'),
        moment(this.datePickerEnd).format('YYYY-MM-DD'),
        response => {
          if (response && response.success === false) {
            this.additionalDataOptions = []
            this.additionalDataError = (response.data && response.data.error) || response.error || 'Der opstod en fejl'
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          if (response && response.error) {
            this.additionalDataOptions = []
            this.additionalDataError = response.error
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          if (response && response.data && response.data.error) {
            this.additionalDataOptions = []
            this.additionalDataError = response.data.error
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          this.additionalDataError = null
          let parsedData = response
          if (response && response.response && typeof response.response === 'string') {
            try {
              parsedData = JSON.parse(response.response)
            } catch (e) {
              console.error('Failed to parse response.response', e)
            }
          }
          let cases = null
          if (parsedData && parsedData.Body) {
            if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Maternity') {
              cases = parsedData.Body.GetMaternityCasesResponse_MT && parsedData.Body.GetMaternityCasesResponse_MT.MaternityCase
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Reason') {
              const reasonsResponse = parsedData.Body.GetReasonsResponse_MT || parsedData.Body.GetReasonsResponse_RT
              cases = reasonsResponse && reasonsResponse.Reason
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'WorkInjury') {
              cases = parsedData.Body.GetWorkAccidentCasesResponse_MT && parsedData.Body.GetWorkAccidentCasesResponse_MT.AccidentCase
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Child') {
              cases = parsedData.Body.GetChildrenResponse_MT && parsedData.Body.GetChildrenResponse_MT.Child
            }
          }
          if (Array.isArray(cases)) {
            this.additionalDataOptions = cases
          } else if (cases && cases.Item) {
            this.additionalDataOptions = Array.isArray(cases.Item) ? cases.Item : [cases.Item]
          } else if (cases && typeof cases === 'object') {
            this.additionalDataOptions = [cases]
          } else {
            this.additionalDataOptions = []
          }
          this.selectAdditionalDataAfterFetch()
          this.isLoadingAdditionalData = false
          this.hasLoadedAdditionalData = true
        },
        error => {
          console.error('fetchAdditionalDataForAbsence error', error)
          this.additionalDataOptions = []
          if (error && error.data && error.data.error) {
            this.additionalDataError = error.data.error
          } else if (error && error.error) {
            this.additionalDataError = error.error
          } else {
            this.additionalDataError = 'Der opstod en fejl ved hentning af data'
          }
          this.isLoadingAdditionalData = false
          this.hasLoadedAdditionalData = true
        }
      )
    },
    selectAdditionalDataAfterFetch () {
      const existing = this.existingAdditionalData
      if (existing) {
        this.selectedAdditionalData = existing
        return
      }
      if (this.additionalDataOptions.length !== 1) return
      var singleOption = this.additionalDataOptions[0]
      var additionalDataType = this.getOPUSAbsence && this.getOPUSAbsence.additional_data
      if (additionalDataType === 'Maternity' && singleOption.MaternityCaseKey) {
        this.selectedAdditionalData = singleOption.MaternityCaseKey
      } else if (additionalDataType === 'Reason' && singleOption.ReasonCode) {
        this.selectedAdditionalData = singleOption.ReasonCode
      } else if (additionalDataType === 'WorkInjury' && singleOption.AccidentNumber) {
        this.selectedAdditionalData = singleOption.AccidentNumber
      } else if (additionalDataType === 'Child' && singleOption.ChildNumber) {
        this.selectedAdditionalData = singleOption.ChildNumber
      }
    },
    closeSchedulerPopover () {
      this.$store.commit('setActiveUser', null)
      this.$store.commit('setActiveEvent', null)
      this.$store.dispatch('setActiveSchedulerEvent', {
        'left': -10,
        'right': -10,
        'color': '',
        'oldSubScoreMeta': [],
        'obj': null
      })
    },
    initFromAbsenceReason () {
      if (!this.activeAbsenceReason) return
      let startTime = this.getSerialListItem ? this.getSerialListItem.from_time : this.activeAbsenceReason.start
      this.startTime = moment(startTime).locale('LT')
      let endTime = this.getSerialListItem ? this.getSerialListItem.to_time : this.activeAbsenceReason.end
      this.endTime = moment(endTime).locale('LT')
      let idx = this.absenceReasons.findIndex(absenceSubject => absenceSubject.id === this.activeAbsenceReason.subject_id)
      if (idx > -1) {
        this.reason = [this.absenceReasons[idx]]
      }
      if (this.activeAbsenceReason.event_serial_id) {
        let reason = this.longAbsentListInHash[this.activeAbsenceReason.event_serial_id]
        if (reason) {
          this.originalStartAndEnd = [reason.from_date, reason.to_date]
          this.isAbsenceOngoing = reason.ongoing
        }
      } else if (this.activeAbsenceReason.from_date) {
        this.originalStartAndEnd = [new Date(this.activeAbsenceReason.from_date), new Date(this.activeAbsenceReason.to_date)]
      } else {
        this.originalStartAndEnd = [this.activeAbsenceReason.start, this.activeAbsenceReason.end]
      }
      // Pre-select employment_id fra activeAbsenceReason eller serial-listen
      const employmentId = this.activeAbsenceReason.employment_id ||
        (this.getSerialListItem && this.getSerialListItem.employment_id)
      if (employmentId) {
        this.selectedEmploymentId = employmentId
      }
      const serialItem = this.getSerialListItem
      this.useTime = !!(serialItem && serialItem.use_time)
      if (this.isAbsenceIntegrationActive) {
        this.$store.dispatch('readEmployments')
        this.listKmdOpusAbsenceCodes()
      }
      if (this.isSDIntegrationActive) {
        this.listSdAbsenceCodes()
        this.readSdFravaerForSerial()
      }
    },
    // Henter fraværets SD-indberetninger, så vi kan advare ved sletning/redigering.
    // Kun fallback: bruges ikke når list-payloaden allerede indeholder sd_fravaer.
    // Fejler stille (fx hvis brugeren ikke har lønrettigheder) - så vises advarslen bare ikke.
    readSdFravaerForSerial () {
      const serialId = this.activeAbsenceReason && this.activeAbsenceReason.event_serial_id
      if (!serialId || !this.schoolId) return
      const serialItem = this.getSerialListItem
      if (serialItem && Array.isArray(serialItem.sd_fravaer)) return
      // Bredt interval omkring fraværet, så også redigerede/åbenstående indberetninger fanges
      const start = moment(serialItem && serialItem.from_date ? serialItem.from_date : undefined).subtract(1, 'year').format('YYYY-MM-DD')
      const end = moment(serialItem && serialItem.to_date ? serialItem.to_date : undefined).add(1, 'year').format('YYYY-MM-DD')
      docendo.listSilkeborgDataFravaer(this.schoolId, start, end, success => {
        const rows = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
        this.sdFravaerForSerial = rows.filter(row => row.event_serial_id === serialId)
      }, failure => {
        console.log('listSilkeborgDataFravaer failure', failure)
      })
    }
  },
  beforeMount () {
    this.initFromAbsenceReason()
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal__body {
  width: 700px;
  max-width: 95vw;
  padding: 0;
}

.d-modal__body--edit-absence--with-opus {
  width: 1000px;
  max-width: 95vw;
}

.d-modal__header {
  margin-bottom: 0;
}

.absent-user__name {
  flex: 1;
}

.absent-user__name p {
  font-weight: bold;
  font-size: 1rem;
}

.absent-user__name h3 > span {
  font-size: 1rem;
}

.d-modal__body--edit-absence .create__form-group {
  margin: 2rem 0;
}

.delete-absence-button {
  margin-left: 2rem;
}

.d-modal__body--edit-absence {
  max-height: 80vh;
  overflow-y: scroll;
  padding: 0;
}

.d-modal__body--edit-absence__header {
  display: flex;
  padding: 2rem;
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 0.5rem;
}

.d-modal__body--edit-absence__header img {
  width: 45px;
  height: 45px;
  border-radius: 4px;
  margin-right: 1rem;
}

.reason {
  display: flex;
  flex: 1;
  justify-content: space-between;
}

.d-modal__body--edit-absence__body {
  padding: 2rem 2rem 1rem 2rem;
}

.d-modal__body--edit-absence__actions {
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.absence-no-end-date {
  display: flex;
  align-items: center;
  margin-left: 1rem;
  user-select: none;
  position: relative;
}

.absence-no-end-date.absence-no-end-date--disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.absence-no-end-date--not-allowed {
  cursor: not-allowed;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background: rgb(255 255 255 / 50%);
  z-index: 11212122;
  border-radius: 50px;
  pointer-events: none;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-weight: bold;
}

.absence-no-end-date.absence-no-end-date--active label {
  color: var(--blue);
}

.absence-no-end-date label {
  margin-bottom: 0;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
}

.absence-no-end-date__inner {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.d-modal__body--edit-absence__body__input-group {
  margin-bottom: 1rem;
}

.error-message {
  color: #ff3860;
  font-size: 11px;
  font-weight: 400;
  padding: 1px 2px 2px;
}

/* Flex layout for opus sidebar */
.d-modal__body--edit-absence__flex {
  display: flex;
  flex-direction: row;
}

.d-modal__body--edit-absence__left {
  flex: 1;
  min-width: 0;
}

/* Opus sidebar */
.edit-absence__opus-sidebar {
  background-color: #fefce8;
  max-width: 300px;
  width: 100%;
  padding: 1.5rem 1rem;
  flex-shrink: 0;
  border-left: 1px solid #facc17;
}

.edit-absence__opus-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.edit-absence__opus-content + .edit-absence__opus-content {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #fde047;
}

.opus-info__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #713f12;
  margin: 0 0 1.25rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #fde047;
}

.opus-info__field {
  margin-bottom: 1rem;
}

.opus-info__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.5rem;
}

.opus-info__label--secondary {
  font-size: 0.6875rem;
  margin-bottom: 0.375rem;
}

.opus-info__reason-display {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.opus-info__reason-code {
  font-size: 0.75rem;
  color: #92400e;
}

.opus-info__extra-info {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #fde047;
}

.opus-info__date-text {
  font-size: 0.875rem;
  color: #78350f;
  margin-bottom: 0.5rem;
}

.opus-info__date-days {
  color: #92400e;
}

.opus-info__toggle-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.opus-info__toggle-row--secondary {
  margin-top: 0.5rem;
}

.opus-info__toggle-row .do-opus-whole-day-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.25rem 1rem;
  border-radius: 4px;
  flex-direction: row-reverse;
  justify-content: space-between;
  width: 100%;
  box-shadow: 0 0 0 0 transparent, 0 0 0 0 transparent, 0 1px 1px 0 rgba(0, 0, 0, .12), 0 0 0 1px rgba(64, 68, 82, .16), 0 0 0 0 transparent, 0 0 0 0 transparent, 0 2px 5px 0 rgba(64, 68, 82, .08);
  background: #fff;
}

.do-opus-whole-day-toggle label {
  margin-bottom: 0;
  cursor: pointer;
}

.opus-info__toggle-row .do-opus-whole-day-toggle span {
  font-size: 0.875rem;
  color: #78350f;
}

.opus-info__note {
  font-size: 0.875rem;
  color: #92400e;
  font-style: italic;
  margin: 0;
}

.opus-info__note--muted {
  font-size: 0.8125rem;
  color: #a1a1aa;
  font-style: italic;
  margin: 0;
}

.opus-info__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #dc2626;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

.opus-info__error-icon {
  font-size: 1rem;
}

.do-opus-badge,
.do-sd-badge {
  display: inline-flex;
  font-weight: 400;
  border: 1px solid #facc17;
  border-radius: 4px;
  background-color: #fefce8;
  padding: 3px 4px;
  font-size: 9px;
  line-height: 1.5;
  color: #a26206;
  margin-left: auto;
}

.do-opus-badge--large {
  font-size: 11px;
  padding: 4px 8px;
  margin-left: 0;
}

.do-select {
  width: 100%;
}

@media (max-width: 1050px) {
  .d-modal__body--edit-absence__flex {
    flex-direction: column;
  }
  .edit-absence__opus-sidebar {
    max-width: 100%;
    border-left: none;
    border-top: 1px solid #facc17;
  }
}

@media (max-width: 768px) {
  .d-modal__body--edit-absence__header {
    padding: 1rem;
  }
  .d-modal__body--edit-absence__body {
    padding: 1rem;
  }
  .d-modal__body--edit-absence__actions {
    padding: 0.5rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .d-modal__body--edit-absence__actions__right {
    display: flex;
    gap: 0.5rem;
  }
  .reason {
    flex-direction: column;
    gap: 0.25rem;
  }
  .flex {
    flex-direction: column;
  }
  .absence-no-end-date {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}
</style>



// WEBPACK FOOTER //
// SubsEditAbsence.vue?1f82fb1c